// Concierge to-do list for one athlete (spec 2026-09-19-concierge-todo-task-model-design.md).
// ONE axis: time. Every task carries a real due date, so the list is bucketed
// Overdue / This week / Later and nothing else. "Waiting" is a badge on the row,
// not a bucket — an overdue task that is waiting on someone is still overdue,
// and burying it in a Waiting section is the bug this rework exists to fix.
// Creation goes through AddTaskDialog, closing a task through DoneDialog (which
// is where the next occurrence's date is chosen). Every write re-reads the list.
// Styled with the concierge ledger classes (ConciergePage.css .cn-todo*).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  bucketTasks, describeEvent, describeTask, dueLabel, isOverdue, todoErrorText, utcTodayIso,
} from '../../lib/conciergeTodoModel';
import AddTaskDialog from './AddTaskDialog';
import DoneDialog from './DoneDialog';
import MilestoneTimeline from './MilestoneTimeline';

const BUCKETS = [
  ['overdue', 'Overdue'],
  ['upcoming', 'This week'],
  ['later', 'Later'],
];

const CLOSED = ['done', 'na'];

// Which collapsible sections are open, remembered per browser rather than per
// athlete: staff work a list of athletes and want the same shape on each one.
// Both start closed. Storage can throw or come back empty (private window,
// blocked site data), which just means "all closed".
const SECTIONS_KEY = 'cn.todo.sections';

function readSections() {
  try {
    const raw = JSON.parse(localStorage.getItem(SECTIONS_KEY) || '{}');
    return { done: raw.done === true, history: raw.history === true };
  } catch (e) {
    return { done: false, history: false };
  }
}

function writeSections(sections) {
  try {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(sections));
  } catch (e) {
    /* storage blocked: the choice just doesn't survive the reload */
  }
}

// A drawn chevron rather than a ▸/▾ text glyph, so the caret scales with the
// header font and rotates instead of swapping characters.
function Chevron() {
  return (
    <svg className="cn-todo-group__chev" viewBox="0 0 12 12" width="12" height="12"
         aria-hidden="true" focusable="false">
      <path d="M4.5 2 L8.5 6 L4.5 10" fill="none" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionToggle({ open, onClick, label, count }) {
  return (
    <button type="button" className="cn-todo-group__toggle" aria-expanded={open} onClick={onClick}>
      <Chevron />
      <span>{label}</span>
      {count && <span className={`cn-todo-group__count${count.urgent ? ' cn-todo-group__count--overdue' : ''}`}>{count.text}</span>}
    </button>
  );
}

// The note lives on the row, not inside the ⋯ menu — staff have to see it
// without opening anything. Click the text (or "Note" when empty) to edit.
// Once open it STAYS open: collapsing on blur would move every row below it
// out from under a click that is already on its way down.
function NoteBlock({ item, readOnly, onSend }) {
  const [note, setNote] = useState(item.note || '');
  const [editing, setEditing] = useState(false);
  const box = useRef(null);
  useEffect(() => setNote(item.note || ''), [item.note]);
  useEffect(() => { if (editing && box.current) box.current.focus(); }, [editing]);
  const text = (item.note || '').trim();

  if (readOnly) {
    return text ? <p className="cn-todo__note-text">{item.note}</p> : null;
  }
  if (editing) {
    return (
      <textarea
        ref={box}
        className="ae-field__input cn-todo__note"
        aria-label={`Note for ${item.title}`}
        placeholder="Note"
        rows={2}
        value={note}
        onChange={e => setNote(e.target.value)}
        // NOT guarded by the panel's in-flight lock — see the note on
        // `inflight` in TodoPanel: a guarded blur-save swallows the next click.
        onBlur={() => { if (note !== (item.note || '')) onSend(item, { note }); }}
      />
    );
  }
  return (
    <button
      type="button"
      className={text ? 'cn-todo__note-text cn-todo__note-text--btn' : 'cn-todo__note-add'}
      aria-label={text ? `Edit note for ${item.title}` : `Add a note for ${item.title}`}
      onClick={() => setEditing(true)}
    >{text || '✎ Note'}</button>
  );
}

function TodoRow({ item, today, readOnly, saving, expanded, onToggle, onSend, onCheck, onDelete }) {
  // The date input fires onChange for every partial year it passes through
  // (0002, 0020, 0201...), so hold the typed value and save on blur / Enter.
  const [due, setDue] = useState(item.due_at || '');
  useEffect(() => setDue(item.due_at || ''), [item.due_at]);
  const commitDue = () => {
    if (due === (item.due_at || '')) return;
    // A task always has a date (spec §2.1) and the server refuses to clear one,
    // so an emptied field just snaps back rather than sending a doomed write.
    if (!due) { setDue(item.due_at || ''); return; }
    // A rejected date (e.g. outside 2000-2100) goes back to the saved value.
    onSend(item, { due_at: due }).then(ok => { if (!ok) setDue(item.due_at || ''); });
  };
  const closed = CLOSED.includes(item.status);
  // `status` is only open/waiting/na/done, so the overdue colour has to be
  // applied from the date — .cn-todo--overdue is the one rule that reddens a row.
  const overdue = isOverdue(item, today);

  return (
    <li
      className={`cn-todo cn-todo--${item.status}${overdue ? ' cn-todo--overdue' : ''}`}
      data-testid={`todo-row-${item.id}`}
    >
      <div className="cn-todo__main">
        <button
          type="button"
          className="cn-todo__check"
          aria-label={closed ? `Reopen ${item.title}` : `Close ${item.title}`}
          aria-pressed={closed}
          disabled={readOnly || saving}
          onClick={() => onCheck(item)}
        >{closed ? '✓' : ''}</button>
        <div className="cn-todo__text">
          <div className="cn-todo__title">
            {item.title}
            {item.is_recurring && (
              <span className="cn-todo__recur" role="img" aria-label="Repeats">◎</span>
            )}
          </div>
          <div className="cn-todo__subline">
            {closed && <span className="cn-todo__sub">{describeTask(item, { withNote: false })}</span>}
            {item.waiting_on && <span className="cn-todo__badge">Waiting on {item.waiting_on}</span>}
          </div>
        </div>
        {!closed && <span className={`cn-todo__duecol cn-todo__duecol--${overdue ? 'overdue' : 'open'}`}>{dueLabel(item, today)}</span>}
        <button
          type="button"
          className="cn-todo__more"
          aria-label={`More options for ${item.title}`}
          aria-expanded={expanded}
          onClick={onToggle}
        >⋯</button>
      </div>
      <div className="cn-todo__notebox">
        <NoteBlock item={item} readOnly={readOnly} onSend={onSend} />
      </div>
      {expanded && (item.description || !readOnly) && (
        <div className="cn-todo__menu">
          {item.description && <p className="cn-todo__desc">{item.description}</p>}
          {!readOnly && (
            <>
              {!closed && (
                <label className="cn-todo__duefield">
                  <span className="cn-todo__duelabel">Due date</span>
                  <input
                    type="date"
                    className="ae-field__input cn-todo__due"
                    aria-label={`Due date for ${item.title}`}
                    value={due}
                    onChange={e => setDue(e.target.value)}
                    onBlur={commitDue}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                  />
                </label>
              )}
              <div className="cn-todo__actions">
                {!closed && (
                  <>
                    <button type="button" className="cn-todo__action" onClick={() => onSend(item, { action: 'waiting', waiting_on: 'family' })}>Waiting on family</button>
                    <button type="button" className="cn-todo__action" onClick={() => onSend(item, { action: 'waiting', waiting_on: 'coach' })}>Waiting on coach</button>
                    <button type="button" className="cn-todo__action" onClick={() => onSend(item, { action: 'na' })}>N/A</button>
                  </>
                )}
                {closed && (
                  <button type="button" className="cn-todo__action" onClick={() => onSend(item, { action: 'reopen' })}>Reopen</button>
                )}
                <button type="button" className="cn-todo__action cn-todo__action--danger" onClick={() => onDelete(item)}>Delete</button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export default function TodoPanel({ userId, onSaved, onNoteAdded }) {
  const { API_URL } = useAuth();
  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const base = `${API_URL}/concierge/${userId}/todos`;

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [sections, setSections] = useState(readSections);
  const toggleSection = (k) => setSections(s => {
    const next = { ...s, [k]: !s[k] };
    writeSections(next);
    return next;
  });
  const [addOpen, setAddOpen] = useState(false);
  const [doneFor, setDoneFor] = useState(null);
  // History is fetched while open, and again after every successful save
  // (historyGen). A response from before the latest save is dropped. Its open
  // state is remembered, so a staffer who left it open pays the fetch on load.
  const historyOpen = sections.history;
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [historyGen, setHistoryGen] = useState(0);
  const loadedGen = useRef(-1);   // the historyGen the shown history belongs to
  // Every write goes through one queue, so responses land in the order the
  // writes were made and the last full-list response reflects all of them.
  const queue = useRef(Promise.resolve());
  const queued = useRef(0);
  // Guarded (submit-style) writes in flight: ✓, the Done dialog's Save, Add
  // task. The ref is the real guard (a second click in the same tick must not
  // send); the state drives the disabled buttons. Field saves on blur are NOT
  // guarded — in a browser the mousedown on ✓ blurs the field first, and a
  // guarded blur-save would swallow that click.
  const inflight = useRef(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(base, cfg())
      .then(res => setData(res.data))
      .catch(err => setError(todoErrorText(err, 'Failed to load the to-do list.')));
    // eslint-disable-next-line
  }, [userId, API_URL]);

  useEffect(() => {
    if (!historyOpen || loadedGen.current === historyGen) return undefined;
    let live = true;
    setHistoryError('');
    axios.get(`${base}/history`, cfg())
      .then(res => {
        if (!live) return;
        loadedGen.current = historyGen;
        setHistory(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => { if (live) setHistoryError(todoErrorText(err, 'Failed to load history.')); });
    return () => { live = false; };
    // eslint-disable-next-line
  }, [historyOpen, historyGen, userId, API_URL]);

  // Send now when nothing is queued (so a click's request goes out in the same
  // tick), otherwise after the write ahead of it, whether that one failed or not.
  const enqueue = (makeReq) => {
    const run = queued.current === 0 ? makeReq() : queue.current.then(makeReq, makeReq);
    queued.current += 1;
    queue.current = run.catch(() => {}).finally(() => { queued.current -= 1; });
    return run;
  };

  // Every write returns the full list; render straight from the response.
  const settle = (makeReq, { guard = false } = {}) => {
    setError('');
    if (guard) { inflight.current += 1; setSaving(true); }
    return enqueue(makeReq)
      .then(res => { setData(res.data); setHistoryGen(g => g + 1); if (onSaved) onSaved(); return true; })
      .catch(err => { setError(todoErrorText(err, 'Save failed.')); return false; })
      .finally(() => {
        if (!guard) return;
        inflight.current -= 1;
        if (inflight.current === 0) setSaving(false);
      });
  };
  const busy = () => inflight.current > 0;

  // Every task is a real row now, so there is one write route: PATCH by id.
  const send = (t, body, opts) => settle(() => axios.patch(`${base}/${t.id}`, body, cfg()), opts);

  const check = (t) => {
    if (busy()) return;
    if (CLOSED.includes(t.status)) { send(t, { action: 'reopen' }, { guard: true }); return; }
    // A repeat exists because somebody picked the next date, not because a
    // formula fired — so a recurring task always stops at the dialog.
    if (t.is_recurring) { setDoneFor(t); return; }
    send(t, { action: 'done' }, { guard: true });
  };

  const saveDone = (body) => {
    if (busy() || !doneFor) return;
    send(doneFor, { action: 'done', ...body }, { guard: true })
      .then(ok => { if (ok) { setDoneFor(null); if (body.summary_body && onNoteAdded) onNoteAdded(); } });
  };

  const addTask = (body) => {
    if (busy()) return;
    settle(() => axios.post(base, body, cfg()), { guard: true })
      .then(ok => { if (ok) setAddOpen(false); });
  };

  const remove = (t) => settle(() => axios.delete(`${base}/${t.id}`, cfg()));

  // Milestone endpoints are siblings of /todos, not children of it -- build
  // from API_URL directly rather than appending to `base`.
  const recordMilestone = (body) => settle(
    () => axios.post(`${API_URL}/concierge/${userId}/milestones`, body, cfg()), { guard: true });
  const removeMilestone = (m) => settle(
    () => axios.delete(`${API_URL}/concierge/${userId}/milestones/${m.id}`, cfg()), { guard: true });

  if (!data) {
    return (
      <>
        <div className="cn-sechead"><span className="cn-sechead__title">To-do</span></div>
        {error ? <span className="cn-inline-msg cn-inline-msg--err">{error}</span>
          : <div className="cn-loading">Loading…</div>}
      </>
    );
  }

  const readOnly = data.read_only;
  const today = utcTodayIso();
  const g = bucketTasks(data.tasks, today);
  // The server 409s on a template that already has an open OR waiting row, so
  // both have to grey out in the picker or the add is offered and then refused.
  const openKeys = (data.tasks || [])
    .filter(t => t.template_key && (t.status === 'open' || t.status === 'waiting'))
    .map(t => t.template_key);
  const rowProps = (t) => ({
    item: t, today, readOnly, saving,
    expanded: expanded === t.id,
    onToggle: () => setExpanded(expanded === t.id ? null : t.id),
    onSend: send, onCheck: check, onDelete: remove,
  });
  const c = data.counts;
  const empty = g.overdue.length + g.upcoming.length + g.later.length === 0;

  const open = g.overdue.length + g.upcoming.length + g.later.length;

  return (
    <div className="cn-todo-split">
      <section className="cn-card">
        <div className="cn-card__head">
          <h2 className="cn-card__title">To-do</h2>
          <span className="cn-card__sub">
            {open} open · {c.overdue} overdue · {c.waiting} waiting
            {readOnly ? ' · read-only (no longer concierge)' : ''}
          </span>
          {!readOnly && !addOpen && (
            <button type="button" className="cn-dbtn" onClick={() => setAddOpen(true)}>+ Add task</button>
          )}
        </div>
        {error && <div className="cn-card__err">{error}</div>}
        {!readOnly && addOpen && (
          <AddTaskDialog
            templates={data.templates || []}
            openKeys={openKeys}
            saving={saving}
            onCancel={() => setAddOpen(false)}
            onSubmit={addTask}
          />
        )}
        {doneFor && (
          <DoneDialog task={doneFor} today={today} prompts={data.summary_prompts} saving={saving}
                      onCancel={() => setDoneFor(null)} onSubmit={saveDone} />
        )}
        {empty && <div className="cn-empty-line">Nothing on the list.</div>}
        {BUCKETS.map(([k, label]) => (g[k].length > 0 && (
          <div key={k} className={`cn-todo-group cn-todo-group--${k}`} data-testid={`todo-bucket-${k}`}>
            <h4 className={`cn-group cn-todo-group__title cn-todo-group__title--${k}`}>{label}</h4>
            <ul className="cn-todo-list">{g[k].map(t => <TodoRow key={t.id} {...rowProps(t)} />)}</ul>
          </div>
        )))}
        {g.closed.length > 0 && (
          <div className="cn-todo-group" data-testid="todo-group-done">
            <SectionToggle open={sections.done} onClick={() => toggleSection('done')}
                           label="Done & N/A" count={{ text: `(${g.closed.length})` }} />
            {sections.done && (
              <ul className="cn-todo-list">{g.closed.map(t => <TodoRow key={t.id} {...rowProps(t)} />)}</ul>
            )}
          </div>
        )}
      </section>
      <aside className="cn-todo-aside">
        <section className="cn-card">
          <div className="cn-card__head"><h2 className="cn-card__title">Milestones</h2></div>
          {data.milestones && data.milestones.length > 0
            ? (
              <MilestoneTimeline milestones={data.milestones} readOnly={readOnly} saving={saving}
                                 onRecord={recordMilestone} onRemove={removeMilestone} />
            )
            : <div className="cn-empty-line">No milestones.</div>}
        </section>
        <section className="cn-card" data-testid="todo-group-history">
          <div className="cn-card__head">
            <SectionToggle open={historyOpen} onClick={() => toggleSection('history')} label="History" />
          </div>
          {historyOpen && (historyError
            ? <div className="cn-card__err">{historyError}</div>
            : history === null ? <div className="cn-loading">Loading…</div>
              : history.length === 0 ? <div className="cn-todo-history__empty">No history yet.</div>
                : (
                  <ul className="cn-todo-history" data-testid="todo-history">
                    {history.map(e => <li key={e.id} className="cn-todo-history__line">{describeEvent(e)}</li>)}
                  </ul>
                ))}
        </section>
      </aside>
    </div>
  );
}
